import { useState } from 'react';
import { Calendar, Clock, GraduationCap, UserRound, Video } from 'lucide-react';
import { toast } from 'react-toastify';
import UserService from '../../../services/user.service';
import { startPersonalCheckout } from '../../../utils/personalCheckout';

function slotList(value: unknown): any[] {
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
        try {
            return slotList(JSON.parse(value));
        } catch {
            return [];
        }
    }
    if (value && typeof value === 'object') return Object.values(value);
    return [];
}

function statusLabel(status: string) {
    if (status === 'active') return { text: 'Active', className: 'bg-emerald-400/90 text-emerald-950' };
    if (status === 'payment_due' || status === 'renewal_due') return { text: 'Renewal due', className: 'bg-amber-300/90 text-amber-950' };
    if (status === 'paused') return { text: 'Paused', className: 'bg-slate-300/90 text-slate-800' };
    if (status === 'cancelled') return { text: 'Cancelled', className: 'bg-slate-300/90 text-slate-800' };
    if (status === 'expired') return { text: 'Expired', className: 'bg-rose-400/90 text-rose-950' };
    return { text: 'Unpaid', className: 'bg-amber-300/90 text-amber-950' };
}

export default function PersonalClassCard({
    item,
    onUpdated,
    onActivate,
}: {
    item: any;
    onUpdated?: () => void;
    onActivate?: (item: any) => void;
}) {
    const userService = new UserService();
    const [confirmJoin, setConfirmJoin] = useState(false);
    const [joining, setJoining] = useState(false);
    const badge = statusLabel(item?.paymentStatus);

    const completeRegistration = async () => {
        setJoining(true);
        try {
            const response = await userService.payPersonalClass(item.id);
            if (!response?.status) {
                toast.error(response?.message || 'Unable to open checkout');
                return;
            }
            startPersonalCheckout(response.data);
        } catch (err: any) {
            toast.error(err?.message || 'Unable to open checkout');
        } finally {
            setJoining(false);
        }
    };

    const openClass = async () => {
        setJoining(true);
        try {
            const response = await userService.joinPersonalClass(item.id);
            const link = response?.data?.classLink;
            if (!response?.status || !link) {
                toast.error(response?.message || 'Unable to join this class');
                return;
            }
            window.open(link, '_blank', 'noopener,noreferrer');
            setConfirmJoin(false);
        } catch (err: any) {
            toast.error(err?.message || 'Unable to join this class');
        } finally {
            setJoining(false);
        }
    };
    const teacher = [item?.teacher?.firstName, item?.teacher?.lastName].filter(Boolean).join(' ') || 'Not assigned yet';
    const paidUntil = item?.paymentStatus === 'paused'
        ? (item.pausedRemainingLabel || 'Paused')
        : (item?.paidUntil ? new Date(item.paidUntil).toLocaleDateString() : '—');
    const childName = [item?.child?.firstName, item?.child?.lastName].filter(Boolean).join(' ');

    return (
        <article className="overflow-hidden rounded-2xl border border-[#d7e7f5] bg-white shadow-[0_8px_30px_rgba(20,80,140,0.08)]">
            <div className="bg-gradient-to-br from-[#14507a] via-[#1d6b9a] to-[#0e3a58] px-5 py-5 text-white">
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-white/80">Private lesson</p>
                        <h3 className="mt-1 text-xl font-semibold">{childName}</h3>
                        <p className="mt-1.5 flex items-center gap-1.5 text-[17px] font-semibold leading-snug text-white sm:text-[18px]">
                            <GraduationCap size={18} className="shrink-0 opacity-90" />
                            <span>
                                {item?.subject || item?.package?.title}
                                {item?.partner?.organizationName ? (
                                    <span className="font-normal text-white/80"> · {item.partner.organizationName}</span>
                                ) : null}
                            </span>
                        </p>
                    </div>
                    <span className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase ${badge.className}`}>{badge.text}</span>
                </div>
            </div>
            <div className="space-y-3 px-5 py-4">
                <div className="grid grid-cols-2 gap-2 text-[13px]">
                    <div className="rounded-xl border border-[#e7eef5] px-3 py-2">
                        <p className="text-[10px] uppercase tracking-wide text-[#94a3b8]">Grade</p>
                        <p className="font-semibold text-[#334155]">{item?.grade}</p>
                    </div>
                    <div className="rounded-xl border border-[#e7eef5] px-3 py-2">
                        <p className="text-[10px] uppercase tracking-wide text-[#94a3b8]">{item?.paymentStatus === 'paused' ? 'Time saved' : 'Paid through'}</p>
                        <p className="font-semibold text-[#334155]">{paidUntil}</p>
                    </div>
                </div>
                <div className="rounded-xl border border-[#e7eef5] px-3 py-2">
                    <p className="mb-1 flex items-center gap-1 text-[10px] uppercase tracking-wide text-[#94a3b8]">
                        <Calendar size={12} /> Class times ({item?.classesPerWeek} per week)
                    </p>
                    {slotList(item?.slots).map((slot: any, index: number) => (
                        <p key={index} className="flex items-start gap-1.5 text-[13px] text-[#334155]">
                            <Clock size={13} className="mt-0.5 shrink-0 text-[#1d6b9a]" />
                            <span>{slot.localLabel}</span>
                        </p>
                    ))}
                </div>
                <p className="flex items-center gap-1.5 text-[13px] text-[#334155]">
                    <UserRound size={14} className="text-[#1d6b9a]" />
                    Teacher: {teacher}
                </p>
                <p className="text-[12px] text-[#8b9aab]">
                    {item?.paymentStatus === 'paused'
                        ? `Paused. ${item.pausedRemainingLabel || 'The remaining paid time'} stays saved until you resume.`
                        : item?.parentCancelled && item?.hasAccess
                        ? `Registration is cancelled. Class stays open until ${paidUntil}.`
                        : 'The cycle starts the Monday after registration. An invoice for the next cycle is sent at the start of the last week.'}
                </p>
                <div className="flex flex-wrap gap-2">
                    {item?.hasAccess ? (
                        item?.teacher ? (
                            <button
                                type="button"
                                onClick={() => {
                                    if (item.accessMode === 'grace') {
                                        setConfirmJoin(true);
                                        return;
                                    }
                                    void openClass();
                                }}
                                className="inline-flex items-center gap-1.5 rounded-xl bg-[#14507a] px-3.5 py-2 text-[12px] font-semibold text-white"
                            >
                                <Video size={14} /> Join class
                            </button>
                        ) : (
                            <span className="group relative inline-flex">
                                <button
                                    type="button"
                                    disabled
                                    className="inline-flex cursor-not-allowed items-center gap-1.5 rounded-xl bg-[#14507a]/40 px-3.5 py-2 text-[12px] font-semibold text-white"
                                >
                                    <Video size={14} /> Join class
                                </button>
                                <span className="pointer-events-none absolute bottom-full left-0 z-20 mb-2 hidden w-56 rounded-xl bg-[#1e293b] px-3 py-2 text-left text-[12px] font-normal leading-relaxed text-white group-hover:block">
                                    Teacher not assigned, please contact us at learning@rydlearning.com
                                </span>
                            </span>
                        )
                    ) : null}
                    {confirmJoin ? (
                        <div className="w-full rounded-xl border border-amber-200 bg-amber-50 px-3 py-3 text-[13px] text-amber-950">
                            <p>{item.joinWarning}</p>
                            <div className="mt-3 flex gap-2">
                                <button type="button" className="rounded-lg px-3 py-1.5 font-semibold" onClick={() => setConfirmJoin(false)}>Not now</button>
                                <button type="button" disabled={joining} className="rounded-lg bg-[#14507a] px-3 py-1.5 font-semibold text-white" onClick={() => { void openClass(); }}>
                                    {joining ? 'Opening…' : 'Join this class'}
                                </button>
                            </div>
                        </div>
                    ) : null}
                    {!item?.parentCancelled && item?.paymentStatus !== 'paused' && (!item?.hasAccess || item?.invoiceDue || item?.accessMode === 'grace') ? (
                        <button
                            type="button"
                            disabled={joining}
                            onClick={() => { void completeRegistration(); }}
                            className="rounded-xl border border-[#14507a] px-3.5 py-2 text-[12px] font-semibold text-[#14507a] disabled:opacity-60"
                        >
                            {joining ? 'Opening checkout…' : item?.paymentStatus === 'unpaid' ? 'Complete registration' : 'Pay to continue'}
                        </button>
                    ) : null}
                    {item?.paymentStatus === 'paused' && !item?.parentCancelled ? (
                        <button
                            type="button"
                            disabled={joining}
                            onClick={() => {
                                void (async () => {
                                    setJoining(true);
                                    try {
                                        const response = await userService.resumePersonalClass(item.id);
                                        if (!response?.status) {
                                            toast.error(response?.message || 'Unable to resume this lesson');
                                            return;
                                        }
                                        toast.success(response.message || 'Lesson resumed');
                                        onUpdated?.();
                                    } catch (err: any) {
                                        toast.error(err?.message || 'Unable to resume this lesson');
                                    } finally {
                                        setJoining(false);
                                    }
                                })();
                            }}
                            className="rounded-xl bg-[#14507a] px-3.5 py-2 text-[12px] font-semibold text-white disabled:opacity-60"
                        >
                            Resume{item.pausedRemainingLabel ? ` · ${item.pausedRemainingLabel}` : ''}
                        </button>
                    ) : null}
                    {item?.parentCancelled || item?.paymentStatus === 'cancelled' ? (
                        <button
                            type="button"
                            onClick={() => onActivate?.(item)}
                            className="rounded-xl border border-[#14507a] px-3.5 py-2 text-[12px] font-semibold text-[#14507a]"
                        >
                            Activate
                        </button>
                    ) : null}
                </div>
            </div>
        </article>
    );
}
